// src/components/ChampionshipDisplay.tsx - Updated with inline editing for Final Four and Championship titles
import React, { useState, useRef } from 'react';
import { BracketGame, WinnerWithMetadata } from '../types/types';
import BracketGameComponent from './BracketGame';
import { useConfig } from '../context/ConfigContext';
import { Trophy, Pencil, Check, X } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Input } from './ui/input';
import { Button } from './ui/button';
import { Badge } from './ui/badge';

interface ChampionshipDisplayProps {
  games: BracketGame[];
  knownWinners: WinnerWithMetadata[];
  setGames: React.Dispatch<React.SetStateAction<BracketGame[]>>;
  hoveredTeamId: string | null;
  handleTeamSelect: (gameId: number, team: string) => void;
  handleResetTeam: (gameId: number, teamToReset: string | undefined) => void;
  setHoveredTeamId: (id: string | null) => void;
  getTeamSeed: (teamName: string) => number;
  findSourceGameForTeam: (teamName: string | undefined, currentGameId: number) => number | null;
  isTeamFromKnownWinnerPath: (teamName: string, gameId: number, knownWinners: WinnerWithMetadata[], games: BracketGame[]) => boolean;
  isTeamFromOfficialKnownWinnerPath: (teamName: string, gameId: number, knownWinners: WinnerWithMetadata[], games: BracketGame[]) => boolean;
  // Callback to notify parent of changes
  onBracketChange?: (updatedGames: BracketGame[]) => void;
}

/**
 * Component to display the Final Four, Championship game, and tournament champion.
 */
const ChampionshipDisplay: React.FC<ChampionshipDisplayProps> = ({
  games,
  knownWinners,
  setGames,
  hoveredTeamId,
  handleTeamSelect,
  handleResetTeam,
  setHoveredTeamId,
  getTeamSeed,
  findSourceGameForTeam,
  isTeamFromKnownWinnerPath,
  isTeamFromOfficialKnownWinnerPath,
  onBracketChange
}) => {
  // Get round names from context
  const { roundConfigs, updateRoundName } = useConfig();

  // Get the titles from configuration (index 3 for Final Four, index 4 for Championship Game, index 5 for Champion)
  const finalFourTitle = roundConfigs[3]?.name || "Final Four";
  const championshipTitle = roundConfigs[4]?.name || "Championship Game";
  const championTitle = roundConfigs[5]?.name || "Champion";

  // Inline editing state
  const [isEditingFinalFour, setIsEditingFinalFour] = useState(false);
  const [isEditingChampionship, setIsEditingChampionship] = useState(false);
  const [editedFinalFourTitle, setEditedFinalFourTitle] = useState(finalFourTitle);
  const [editedChampionshipTitle, setEditedChampionshipTitle] = useState(championshipTitle);
  const [isSaving, setIsSaving] = useState(false);
  const inputRefFinalFour = useRef<HTMLInputElement>(null);
  const inputRefChampionship = useRef<HTMLInputElement>(null);

  // Update edited titles when props change
  React.useEffect(() => {
    setEditedFinalFourTitle(finalFourTitle);
  }, [finalFourTitle]);

  React.useEffect(() => {
    setEditedChampionshipTitle(championshipTitle);
  }, [championshipTitle]);

  // Focus input when editing starts
  React.useEffect(() => {
    if (isEditingFinalFour && inputRefFinalFour.current) {
      inputRefFinalFour.current.focus();
      inputRefFinalFour.current.select();
    }
  }, [isEditingFinalFour]);

  React.useEffect(() => {
    if (isEditingChampionship && inputRefChampionship.current) {
      inputRefChampionship.current.focus();
      inputRefChampionship.current.select();
    }
  }, [isEditingChampionship]);

  // Handle editing Final Four title (config index 3)
  const handleEditFinalFour = () => {
    setIsEditingFinalFour(true);
    setEditedFinalFourTitle(finalFourTitle);
  };

  const handleSaveFinalFour = async () => {
    if (!editedFinalFourTitle.trim()) {
      handleCancelFinalFour();
      return;
    }

    setIsSaving(true);
    try {
      await updateRoundName(3, editedFinalFourTitle.trim()); // Index 3 for Round of 4
      setIsEditingFinalFour(false);
    } catch (error) {
      console.error('Error saving Final Four title:', error);
      setEditedFinalFourTitle(finalFourTitle);
    } finally {
      setIsSaving(false);
    }
  };

  const handleCancelFinalFour = () => {
    setEditedFinalFourTitle(finalFourTitle);
    setIsEditingFinalFour(false);
  };

  // Handle editing Championship title (config index 4)
  const handleEditChampionship = () => {
    setIsEditingChampionship(true);
    setEditedChampionshipTitle(championshipTitle);
  };

  const handleSaveChampionship = async () => {
    if (!editedChampionshipTitle.trim()) {
      handleCancelChampionship();
      return;
    }

    setIsSaving(true);
    try {
      await updateRoundName(4, editedChampionshipTitle.trim()); // Index 4 for Championship Game
      setIsEditingChampionship(false);
    } catch (error) {
      console.error('Error saving Championship title:', error);
      setEditedChampionshipTitle(championshipTitle);
    } finally {
      setIsSaving(false);
    }
  };

  const handleCancelChampionship = () => {
    setEditedChampionshipTitle(championshipTitle);
    setIsEditingChampionship(false);
  };

  // Find Final Four games (round 4)
  const finalFourGames = games.filter(g => g.round === 4);

  // Find the championship game (should be round 5)
  const championshipGame = games.find(g => g.round === 5);

  if (!championshipGame) {
    return <div className="text-destructive">Error: Championship game not found</div>;
  }

  const champion = championshipGame.winner;
  const winnerMetadata = championshipGame.id !== undefined &&
                       championshipGame.id < knownWinners.length ?
                       knownWinners[championshipGame.id] : null;

  const isOfficialWinner = winnerMetadata &&
                         winnerMetadata.team === champion &&
                         winnerMetadata.source === 'official';

  const isScenarioWinner = winnerMetadata &&
                        winnerMetadata.team === champion &&
                        winnerMetadata.source === 'scenario';

  // Handle reset for championship
  const handleChampionReset = () => {
    if (!champion) return;

    // Don't allow reset if it's an official winner
    if (isOfficialWinner) return;

    // Special case for champion - only reset the winner without affecting previous rounds
    const updatedGames = [...games];
    const champGame = updatedGames.find(g => g.id === championshipGame.id);
    if (champGame) {
      champGame.winner = undefined;
      setGames(updatedGames);

      // Notify parent of changes
      if (onBracketChange) {
        onBracketChange(updatedGames);
      }
    }
  };

  // Editable title component helper
  const renderEditableTitle = (
    isEditing: boolean,
    editedValue: string,
    displayValue: string,
    inputRef: React.RefObject<HTMLInputElement>,
    onEdit: () => void,
    onSave: () => void,
    onCancel: () => void,
    onChange: (value: string) => void,
  ) => (
    <div className="flex items-center justify-center gap-2 mb-4">
      {isEditing ? (
        <>
          <Input
            ref={inputRef}
            type="text"
            value={editedValue}
            onChange={(e) => onChange(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') onSave();
              if (e.key === 'Escape') onCancel();
            }}
            disabled={isSaving}
            className="h-8 min-w-[200px] max-w-[300px] text-center text-lg font-bold"
          />
          <Button
            variant="ghost"
            size="icon"
            className="h-7 w-7 text-success hover:text-success"
            onClick={onSave}
            disabled={isSaving}
            title="Save"
          >
            <Check className="h-4 w-4" />
          </Button>
          <Button
            variant="ghost"
            size="icon"
            className="h-7 w-7 text-destructive hover:text-destructive"
            onClick={onCancel}
            disabled={isSaving}
            title="Cancel"
          >
            <X className="h-4 w-4" />
          </Button>
        </>
      ) : (
        <>
          <h3 className="text-xl font-bold text-primary">{displayValue}</h3>
          <Button
            variant="ghost"
            size="icon"
            className="h-7 w-7 text-muted-foreground hover:text-foreground"
            onClick={onEdit}
            title="Edit round name"
          >
            <Pencil className="h-3.5 w-3.5" />
          </Button>
        </>
      )}
    </div>
  );

  return (
    <div className="mt-5 space-y-6">
      {/* Final Four Section */}
      <div>
        {renderEditableTitle(
          isEditingFinalFour,
          editedFinalFourTitle,
          finalFourTitle,
          inputRefFinalFour as React.RefObject<HTMLInputElement>,
          handleEditFinalFour,
          handleSaveFinalFour,
          handleCancelFinalFour,
          setEditedFinalFourTitle,
        )}

        <div className="flex justify-between gap-5 mb-8">
          {/* Left side Final Four game */}
          <div className="flex-1 max-w-[350px]">
            {finalFourGames.length > 0 && (
              <BracketGameComponent
                game={finalFourGames[0]}
                knownWinnersWithMetadata={knownWinners}
                hoveredTeamId={hoveredTeamId}
                handleTeamSelect={handleTeamSelect}
                handleResetTeam={handleResetTeam}
                setHoveredTeamId={setHoveredTeamId}
                getTeamSeed={getTeamSeed}
                findSourceGameForTeam={findSourceGameForTeam}
                isTeamFromKnownWinnerPath={isTeamFromKnownWinnerPath}
                isTeamFromOfficialKnownWinnerPath={isTeamFromOfficialKnownWinnerPath}
                games={games}
              />
            )}
          </div>

          {/* Right side Final Four game */}
          <div className="flex-1 max-w-[350px]">
            {finalFourGames.length > 1 && (
              <BracketGameComponent
                game={finalFourGames[1]}
                knownWinnersWithMetadata={knownWinners}
                hoveredTeamId={hoveredTeamId}
                handleTeamSelect={handleTeamSelect}
                handleResetTeam={handleResetTeam}
                setHoveredTeamId={setHoveredTeamId}
                getTeamSeed={getTeamSeed}
                findSourceGameForTeam={findSourceGameForTeam}
                isTeamFromKnownWinnerPath={isTeamFromKnownWinnerPath}
                isTeamFromOfficialKnownWinnerPath={isTeamFromOfficialKnownWinnerPath}
                games={games}
              />
            )}
          </div>
        </div>
      </div>

      {/* Championship Game */}
      <div>
        {renderEditableTitle(
          isEditingChampionship,
          editedChampionshipTitle,
          championshipTitle,
          inputRefChampionship as React.RefObject<HTMLInputElement>,
          handleEditChampionship,
          handleSaveChampionship,
          handleCancelChampionship,
          setEditedChampionshipTitle,
        )}

        <div className="max-w-[350px] mx-auto mb-5">
          <BracketGameComponent
            game={championshipGame}
            knownWinnersWithMetadata={knownWinners}
            hoveredTeamId={hoveredTeamId}
            handleTeamSelect={handleTeamSelect}
            handleResetTeam={handleResetTeam}
            setHoveredTeamId={setHoveredTeamId}
            getTeamSeed={getTeamSeed}
            findSourceGameForTeam={findSourceGameForTeam}
            isTeamFromKnownWinnerPath={isTeamFromKnownWinnerPath}
            isTeamFromOfficialKnownWinnerPath={isTeamFromOfficialKnownWinnerPath}
            games={games}
          />
        </div>
      </div>

      {/* Champion display */}
      <Card className="border-2 border-primary bg-primary/5">
        <CardHeader className="pb-2">
          <CardTitle className="text-xl font-bold text-primary text-center flex items-center justify-center gap-2">
            <Trophy className="h-5 w-5" />
            {championTitle}
            <Trophy className="h-5 w-5" />
          </CardTitle>
        </CardHeader>
        <CardContent className="text-center">
          {champion ? (
            <div className="text-2xl font-bold text-foreground flex items-center justify-center gap-2">
              {champion}
              {isOfficialWinner && (
                <Badge variant="teal" className="text-xs">
                  Official
                </Badge>
              )}
              {isScenarioWinner && (
                <Badge variant="secondary" className="text-xs">
                  Scenario
                </Badge>
              )}
              {!isOfficialWinner && !isScenarioWinner && (
                <button
                  className="inline-flex items-center justify-center h-4 w-4 rounded-full bg-muted-foreground/20 text-muted-foreground hover:bg-muted-foreground/40 transition-colors ml-2"
                  onClick={handleChampionReset}
                >
                  <X className="h-3 w-3" />
                </button>
              )}
            </div>
          ) : (
            <div className="text-muted-foreground">No champion selected</div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default ChampionshipDisplay;
